import { parseDateValue } from '@/services/dateValue';
import { getBrowserMoneyLocale } from '@/utils/amountFormatter';

// One way to render a carrier's values, used by every carrier-import screen.
//
// The app has three money formats and two date formats in live use:
//
//   formatAmount (global filter)   ru-RU, 0-2 decimals      -> "1 234,5"
//   PaymentImportRunModal (local)  Intl, always 2 decimals  -> "1,234.50"
//   the backend's own strings      stripTrailingZeros       -> "1234.5"
//
// A preview that formats one way beside a diff that formats another invites a
// reader to see a change that is not there, which is the exact failure the diff
// work exists to end. So the carrier screens share these, and never compare
// formatted strings -- the backend decides what changed, on the real values.

/**
 * Money as the carrier reports it: grouped, two decimals, no currency symbol.
 *
 * Two decimals always, because a column where some rows show `7500` and others
 * `7500.25` reads as ragged rather than as precise.
 */
export function formatCarrierMoney(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat(getBrowserMoneyLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * A calendar date, never shifted by a timezone.
 *
 * These are date-only fields: the API sends them as instants at UTC midnight,
 * so converting one into a provider's timezone shows the previous day. That has
 * shipped to Mexican providers more than once; see the Dates section of
 * CLAUDE.md and `npm run date:check`.
 */
export function formatCarrierDate(value) {
  if (!value) return '';
  // parseDateValue strips the zone itself, which is what makes it safe here.
  const parsed = parseDateValue(value);
  return parsed && parsed.isValid() ? parsed.format('DD.MM.YYYY') : String(value);
}

/**
 * One value of a diff row, rendered by the kind the backend labelled it with.
 *
 * The kind travels with the value precisely so this decision is not made by
 * guessing at the field's name.
 */
export function formatByKind(value, kind, t) {
  if (value === null || value === undefined || value === '') return '—';
  switch (kind) {
    case 'MONEY':
      return formatCarrierMoney(value);
    case 'DATE':
      return formatCarrierDate(value);
    case 'LIST':
      return Array.isArray(value) && value.length ? value.join('\n') : '—';
    case 'ENUM':
      // Falls back to the raw enum rather than a blank: a value nobody has
      // translated yet is still more use than nothing.
      return t && t(`carriers.import.diff.value.${value}`) !== `carriers.import.diff.value.${value}`
        ? t(`carriers.import.diff.value.${value}`)
        : String(value);
    default:
      return String(value);
  }
}

/**
 * Diff rows the review never shows.
 *
 * - A group member's documents and RFCs: bookkeeping a reviewer cannot check.
 *   The members row itself is shown, by name ([formatMembers]).
 * - Family dependents: no longer saved at all; listed only for an older backend
 *   that still sends them.
 * - The description: carrier text Renova has no use for. Carrier reads no longer
 *   write it; an older backend still sends the row.
 */
export const HIDDEN_DIFF_FIELDS = Object.freeze([
  'memberDocuments', 'memberRfcs',
  'dependents', 'dependentDocuments', 'dependentRfcs',
  'policyDescription',
]);

export function isShownDiffField(field) {
  return !HIDDEN_DIFF_FIELDS.includes(field);
}

/**
 * Fields named by a key the rest of the app already uses, so the review says
 * "Coverage" and "Deal premium" exactly as the policy page does. Anything not
 * here reads `carriers.import.diff.field.<field>`.
 */
const DIFF_FIELD_LABEL_KEYS = Object.freeze({
  product: 'adminTitles.program',
  dealPremium: 'leads.form.dealPremium',
  netPremium: 'leads.form.netPremium',
  // What the carrier lists as coverages are this policy's services in Renova.
  coverages: 'adminTitles.services',
  vehicle: 'adminTitles.VehicleSection',
  savingsBalance: 'policies.savings.Balance',
  savingsContributed: 'policies.savings.Contributed',
  savingsWithdrawals: 'policies.savings.Withdrawals',
  savingsSurrenderValue: 'policies.savings.SurrenderValue',
  savingsPendingContributions: 'policies.savings.MissedContributions',
  savingsContribution: 'policies.savings.PlanContribution',
});

export function diffFieldLabelKey(field) {
  return DIFF_FIELD_LABEL_KEYS[field] || `carriers.import.diff.field.${field}`;
}

/** A field nobody has named yet shows its key rather than a raw i18n path. */
export function diffFieldLabel(field, t, te) {
  const key = diffFieldLabelKey(field);
  return te(key) ? t(key) : field;
}

/**
 * The payments row as counts: what Renova holds, and what Apply would do to it.
 *
 * `counts` = {total, paid, canceled} of the current schedule, and
 * {added, removed, newlyPaid, corrected} of the apply. `tc` is vue-i18n's $tc.
 */
export function formatReceiptCounts(counts, side, tc) {
  const n = (k) => Number(counts && counts[k]) || 0;
  const part = (key, k) => tc(`carriers.import.diff.receipts.${key}`, n(k), { n: n(k) });
  if (side === 'current') {
    const parts = [String(n('total')), part('Paid', 'paid')];
    if (n('canceled') > 0) parts.push(part('Canceled', 'canceled'));
    return parts.join(' · ');
  }
  const parts = [
    ['Added', 'added'], ['NewlyPaid', 'newlyPaid'], ['Corrected', 'corrected'], ['Removed', 'removed'],
  ].filter(([, k]) => n(k) > 0).map(([key, k]) => part(key, k));
  return parts.length ? parts.join(' · ') : tc('carriers.import.diff.receipts.NoChange');
}

/**
 * An older backend's payments string, "12 (7 paid · 0 canceled)", in the words
 * [formatReceiptCounts] uses: "12 · 7 paid", canceled only when there are any.
 * Anything it does not recognise is shown as it came.
 */
export function formatLegacyReceipts(raw, tc) {
  const m = /^(\d+) \((\d+) paid · (\d+) canceled\)(.*)$/.exec(String(raw == null ? '' : raw));
  if (!m) return formatByKind(raw, 'TEXT');
  const [, total, paid, canceled, rest] = m;
  return formatReceiptCounts({ total, paid, canceled }, 'current', tc) + rest;
}

/** How many names each side lists before "…and N more". */
const MEMBERS_SHOWN = 10;

/**
 * Who a group read adds and removes, one per line: "+ ANA LÓPEZ" / "− JUAN PÉREZ".
 * `people` = {added: string[], removed: string[]} from the backend's members row.
 */
export function formatMembers(people, t) {
  const side = (names, sign) => {
    const list = names || [];
    const lines = list.slice(0, MEMBERS_SHOWN).map((n) => `${sign} ${n}`);
    if (list.length > MEMBERS_SHOWN) {
      lines.push(`  …${t('carriers.import.payments.AndMore', { count: list.length - MEMBERS_SHOWN })}`);
    }
    return lines;
  };
  const lines = [...side(people.added, '+'), ...side(people.removed, '−')];
  return lines.length ? lines.join('\n') : t('carriers.import.diff.receipts.NoChange');
}

/**
 * One side ('current' or 'incoming') of a diff row.
 *
 * The payments row renders from its `counts` when the backend sends them, and
 * from its own strings when an older one does not.
 */
export function formatDiffSide(row, side, t, tc) {
  if (!row) return '—';
  if (row.field === 'receipts' && tc) {
    const raw = row[side];
    if (side === 'current' && (raw === null || raw === undefined || raw === '')) return '—';
    if (row.counts) return formatReceiptCounts(row.counts, side, tc);
    return formatLegacyReceipts(raw, tc);
  }
  if (row.field === 'members' && row.people && side === 'incoming' && t) {
    return formatMembers(row.people, t);
  }
  return formatByKind(row[side], row.kind, t);
}

/** The parts of one service line, empty ones dropped: "A · 1000 · · ·" -> ["A", "1000"]. */
function serviceParts(line) {
  return String(line == null ? '' : line)
    .split(/\s*[·—]\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
}

const SUM_INSURED = /^(suma asegurada|sum insured|soma segurada|страховая сумма)\s*:\s*/i;

/**
 * One service as a line of its own: "GASTOS MEDICOS · $1,000,000.00 MXN", its name and sum
 * insured with every empty part left out. Takes either backend's line: the labelled one
 * ("NAME — Suma asegurada: $1,000,000.00 MXN · Deducible: …") or the bare figures of an older
 * one ("NAME · 1000000 · · ·"), whose first figure is the sum insured.
 */
export function serviceLine(line) {
  const parts = serviceParts(line);
  if (!parts.length) return '';
  const [name, ...rest] = parts;
  const labelled = rest.find((part) => SUM_INSURED.test(part));
  let sum = labelled ? labelled.replace(SUM_INSURED, '') : rest.find((part) => !part.includes(':')) || '';
  if (/^-?\d+(\.\d+)?$/.test(sum)) sum = formatCarrierMoney(sum);
  return [name, sum].filter(Boolean).join(' · ');
}

/** A service's figures as one line, the empty ones left out: "Suma asegurada: $1 · Deducible: $5". */
export function serviceFigures(line) {
  return serviceParts(line).join(' · ');
}

/** The service lines of one side of the diff's coverages row, each cleaned (serviceLine). */
export function serviceLines(value) {
  return (Array.isArray(value) ? value : []).map(serviceLine).filter(Boolean);
}

/** What applying would do to one of the policy's services. */
export const SERVICE_ACTIONS = ['ADD', 'UPDATE', 'REMOVE', 'SAME'];

const text = (v) => (v === null || v === undefined ? '' : String(v).trim());

/**
 * One item per service, from the snapshot detail's `services`:
 * `{ name, action, current, incoming }`, in the backend's order.
 *
 * A backend that sends `items` is taken as it is. An older one sends only the
 * `add` / `update` / `remove` names, which become items with no detail text.
 * A name listed twice is kept once, the first time, so it is one checkbox.
 */
export function serviceItems(services) {
  if (!services || typeof services !== 'object') return [];
  let items;
  if (Array.isArray(services.items)) {
    items = services.items
      .filter((it) => it && text(it.name) && SERVICE_ACTIONS.includes(it.action))
      .map((it) => ({
        name: text(it.name), action: it.action, current: text(it.current), incoming: text(it.incoming),
      }));
  } else {
    items = [['add', 'ADD'], ['update', 'UPDATE'], ['remove', 'REMOVE']]
      .flatMap(([key, action]) => (Array.isArray(services[key]) ? services[key] : [])
        .filter((name) => text(name))
        .map((name) => ({
          name: text(name), action, current: '', incoming: '',
        })));
  }
  const seen = new Set();
  return items.filter((it) => {
    if (seen.has(it.name)) return false;
    seen.add(it.name);
    return true;
  });
}

/** A service someone can tick: anything but one that is already the same. */
export const isActionableService = (item) => Boolean(item) && item.action !== 'SAME';

/**
 * The line under a service: what it would become (ADD), what changes (UPDATE),
 * or what goes (REMOVE). Empty when the backend gave no detail.
 */
export function serviceDetail(item) {
  if (!item) return '';
  const current = serviceFigures(item.current);
  const incoming = serviceFigures(item.incoming);
  switch (item.action) {
    case 'ADD': return incoming || '';
    case 'REMOVE': return current || '';
    case 'UPDATE':
      if (current && incoming) return `${current} → ${incoming}`;
      return incoming || current || '';
    default: return incoming || current || '';
  }
}

/**
 * The ticked names that can still take effect, in the items' order: a name no
 * longer offered (or one already the same) is dropped rather than sent.
 */
export function pickedServiceNames(items, picked) {
  const chosen = new Set(picked || []);
  return (items || []).filter((it) => isActionableService(it) && chosen.has(it.name)).map((it) => it.name);
}
